import { Card, Select, Space, Typography } from "antd";
import dayjs from "dayjs";
import {
	Area,
	AreaChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import type { DashboardData } from "@/app/api/types";

const { Title } = Typography;

interface DashboardChartProps {
	data?: DashboardData;
	loading?: boolean;
	year: number;
	month: number;
	onYearChange: (year: number) => void;
	onMonthChange: (month: number) => void;
}

const MONTH_NAMES = [
	"Yanvar",
	"Fevral",
	"Mart",
	"Aprel",
	"May",
	"Iyun",
	"Iyul",
	"Avgust",
	"Sentabr",
	"Oktabr",
	"Noyabr",
	"Dekabr",
];

export function DashboardChart({
	data,
	loading,
	year,
	month,
	onYearChange,
	onMonthChange,
}: DashboardChartProps) {
	const currentYear = dayjs().year();
	const years = Array.from({ length: currentYear - 2024 + 2 }, (_, i) => 2024 + i);
	const months = Array.from({ length: 12 }, (_, i) => i + 1);

	const formatData = () => {
		if (!data?.monthlyAttendance?.days) {
			return [];
		}
		return data.monthlyAttendance.days.map((d) => ({
			date: dayjs(d.date).format("DD"),
			morning: d.morning,
			noon: d.noon,
			night: d.night,
		}));
	};

	return (
		<Card loading={loading} className="mt-6 shadow-sm hover:shadow-md transition-shadow">
			<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
				<Title level={4} style={{ margin: 0 }}>
					Xodimlar davomati grafigi
				</Title>
				<Space>
					<Select
						value={year}
						onChange={onYearChange}
						options={years.map((y) => ({ label: y.toString(), value: y }))}
						style={{ width: 100 }}
						size="large"
					/>
					<Select
						value={month}
						onChange={onMonthChange}
						options={months.map((m) => ({
							label: MONTH_NAMES[m - 1],
							value: m,
						}))}
						style={{ width: 140 }}
						size="large"
					/>
				</Space>
			</div>

			<div className="h-[350px] w-full mt-4">
				{!loading && (
					<ResponsiveContainer width="100%" height="100%">
						<AreaChart data={formatData()} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
							<defs>
								<linearGradient id="colorMorning" x1="0" y1="0" x2="0" y2="1">
									<stop offset="5%" stopColor="#3d6fe0" stopOpacity={0.8} />
									<stop offset="95%" stopColor="#3d6fe0" stopOpacity={0} />
								</linearGradient>
								<linearGradient id="colorNoon" x1="0" y1="0" x2="0" y2="1">
									<stop offset="5%" stopColor="#43a047" stopOpacity={0.8} />
									<stop offset="95%" stopColor="#43a047" stopOpacity={0} />
								</linearGradient>
								<linearGradient id="colorNight" x1="0" y1="0" x2="0" y2="1">
									<stop offset="5%" stopColor="#e8a317" stopOpacity={0.8} />
									<stop offset="95%" stopColor="#e8a317" stopOpacity={0} />
								</linearGradient>
							</defs>
							<XAxis dataKey="date" axisLine={false} tickLine={false} tickMargin={10} />
							<YAxis axisLine={false} tickLine={false} tickMargin={10} />
							<CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
							<Tooltip
								contentStyle={{
									borderRadius: "8px",
									border: "none",
									boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
								}}
								labelStyle={{ fontWeight: "bold", marginBottom: "8px" }}
							/>
							<Area
								type="monotone"
								dataKey="morning"
								name="09:00-10:00"
								stroke="#3d6fe0"
								strokeWidth={2}
								fillOpacity={1}
								fill="url(#colorMorning)"
							/>
							<Area
								type="monotone"
								dataKey="noon"
								name="14:00-15:00"
								stroke="#43a047"
								strokeWidth={2}
								fillOpacity={1}
								fill="url(#colorNoon)"
							/>
							<Area
								type="monotone"
								dataKey="night"
								name="17:00-18:00"
								stroke="#e8a317"
								strokeWidth={2}
								fillOpacity={1}
								fill="url(#colorNight)"
							/>
						</AreaChart>
					</ResponsiveContainer>
				)}
			</div>
		</Card>
	);
}
